import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaExclamationTriangle, FaBan, FaInfoCircle, FaCog } from 'react-icons/fa';
import { API } from '../App';

/**
 * PersonalRecipeAnalysis
 * ──────────────────────
 * Kompakt maskineanalyse for almindelige brugere. Fokuserer på ÉN
 * maskine — nemlig brugerens egen fra `user.machine_name`. Ingen lange
 * lister af 48 maskiner, ingen tekniske advarsler — bare det bruger-
 * relevante svar: passer opskriften? Frysepunkt? Frysetid? Kort råd.
 *
 * Redaktion (admin/editor) bruger fortsat den detaljerede
 * `MachineValidationAlert.js`. Denne komponent kalder det nye endpoint
 * `POST /api/recipe-analysis/personal` som ligger oven på den fælles
 * analyse-motor (services/recipe_analysis.py).
 */
const PersonalRecipeAnalysis = ({ recipe, userMachineName }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!recipe?.target_brix || !recipe?.base_volume_ml) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const payload = {
          recipe: {
            target_brix: parseFloat(recipe.target_brix) || 13,
            base_volume_ml: parseFloat(recipe.base_volume_ml) || 1000,
            recommended_freeze_celsius: recipe.recommended_freeze_celsius,
          },
        };
        if (userMachineName) payload.machine_name = userMachineName;
        const response = await axios.post(
          `${API}/recipe-analysis/personal`,
          payload,
          { withCredentials: true },
        );
        if (!cancelled) setResult(response.data);
      } catch (err) {
        console.error('[PersonalRecipeAnalysis] failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [recipe?.target_brix, recipe?.base_volume_ml, recipe?.recommended_freeze_celsius, userMachineName]);

  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
        Beregner personlig maskineanalyse...
      </div>
    );
  }
  if (!result) return null;

  // ── Ingen maskine valgt ─────────────────────────────────────────────
  if (result.has_machine === false) {
    return (
      <div
        className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3"
        data-testid="personal-analysis-no-machine"
      >
        <FaCog className="text-slate-500 text-lg mt-0.5 shrink-0" />
        <div className="text-sm text-slate-700">
          <div className="font-medium text-slate-800 mb-1">Personlige anbefalinger</div>
          <p>{result.hint || 'Vælg din slushmaskine under Profil for at få personlige anbefalinger.'}</p>
          <Link
            to="/profile"
            data-testid="personal-analysis-go-to-profile"
            className="inline-block mt-2 text-cyan-700 hover:text-cyan-800 font-medium underline"
          >
            Gå til Profil →
          </Link>
        </div>
      </div>
    );
  }

  const analysis = result.analysis || {};
  const { severity, tone, headline, short_advice, freeze_time_min } = analysis;

  // Tone → Tailwind klasser. Én kilde til styling.
  const toneCls = {
    green:  { box: 'bg-emerald-50 border-emerald-200',  bar: 'bg-emerald-100', text: 'text-emerald-800', muted: 'text-emerald-700', icon: FaCheckCircle,          iconCls: 'text-emerald-600' },
    blue:   { box: 'bg-blue-50 border-blue-200',        bar: 'bg-blue-100',    text: 'text-blue-800',    muted: 'text-blue-700',    icon: FaInfoCircle,           iconCls: 'text-blue-600' },
    yellow: { box: 'bg-yellow-50 border-yellow-200',    bar: 'bg-yellow-100',  text: 'text-yellow-900',  muted: 'text-yellow-800',  icon: FaExclamationTriangle,  iconCls: 'text-yellow-600' },
    red:    { box: 'bg-red-50 border-red-200',          bar: 'bg-red-100',     text: 'text-red-800',     muted: 'text-red-700',     icon: FaBan,                  iconCls: 'text-red-600' },
  }[tone] || {
    box: 'bg-slate-50 border-slate-200', bar: 'bg-slate-100', text: 'text-slate-800', muted: 'text-slate-700', icon: FaInfoCircle, iconCls: 'text-slate-500',
  };
  const Icon = toneCls.icon;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${toneCls.box}`}
      data-testid="personal-analysis-card"
      data-severity={severity}
    >
      <div className={`p-4 flex items-start gap-3 ${toneCls.bar}`}>
        <Icon className={`text-xl mt-0.5 shrink-0 ${toneCls.iconCls}`} />
        <div className="flex-1">
          <div className={`font-semibold ${toneCls.text}`} data-testid="personal-analysis-headline">
            {headline}
          </div>
          {short_advice && (
            <div className={`text-sm mt-1 ${toneCls.muted}`} data-testid="personal-analysis-advice">
              {short_advice}
            </div>
          )}
        </div>
      </div>

      {(analysis.recommended_machine_temp_c != null || freeze_time_min != null) && (
        <div className="p-4 flex flex-wrap gap-x-6 gap-y-2 text-sm bg-white/60">
          {analysis.recommended_machine_temp_c != null && (
            <div data-testid="personal-analysis-machine-temp">
              <div className="text-xs uppercase tracking-wide text-gray-500">Anbefalet maskintemperatur</div>
              <div className="font-medium text-gray-800">
                ca. {analysis.recommended_machine_temp_c}°C
                {analysis.freeze_source === 'manual' && (
                  <span className="ml-2 text-xs text-gray-500 font-normal" title="Manuelt sat på opskriften — beregnet værdi vises i redaktør-visning">
                    (manuelt sat)
                  </span>
                )}
              </div>
            </div>
          )}
          {freeze_time_min != null && (
            <div data-testid="personal-analysis-freeze-time">
              <div className="text-xs uppercase tracking-wide text-gray-500">Estimeret frysetid</div>
              <div className="font-medium text-gray-800">ca. {freeze_time_min} minutter</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalRecipeAnalysis;
